import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Alert } from 'react-bootstrap';
import { SessionNote } from './Notes';
import { RoundInfo } from './Notes';
import { AuthState } from '../utils/auth';
import { fetchAPI } from '../utils/api';
import { generateNoteId } from '../utils/notesQueries';
import Labels from './Labels';
import LabelChip from './LabelChip';
import AddLabelDropdown from './AddLabelDropdown';
import LabelFilterDropdown from './LabelFilterDropdown';
import styles from './NotesUI.module.css';

interface Label {
  label: string;
  color: string;
}

interface NotesUIProps {
  sessionNotes: SessionNote[];
  loading: boolean;
  error: string | null;
  onCreateNote: (content: string, vod_time: number, labels?: Record<string, string>, noteId?: string) => Promise<string>;
  onUpdateNote: (note_id: string, content: string, vod_time: number, labels?: Record<string, string>) => Promise<void>;
  onDeleteNote: (note_id: string) => Promise<void>;
  onAddLabelToNote: (noteId: string, labelName: string, labelColor: string) => Promise<void>;
  onRemoveLabelFromNote: (noteId: string, labelName: string) => Promise<void>;
  currentTime: number; // Current video time in seconds
  roundInfo?: RoundInfo; // Optional round timing information
  onNoteClick?: (globalTime: number, roundNumber?: number, youtubeId?: string) => void; // Optional click callback
  authState: AuthState; // Auth state to determine if user can access notes
  // Additional error states from React Query
  createError?: string | null;
  updateError?: string | null;
  deleteError?: string | null;
  // Playback control
  isPlaying?: boolean;
  onPlaybackControl?: (playing: boolean) => void;
  // Freeze timestamp for new notes
  freezeTimeForNewNote?: boolean; // When true, freeze timestamp when user starts typing
}

const NotesUI: React.FC<NotesUIProps> = ({
  sessionNotes,
  loading,
  error,
  onCreateNote,
  onUpdateNote,
  onDeleteNote,
  onAddLabelToNote,
  onRemoveLabelFromNote,
  currentTime,
  roundInfo,
  onNoteClick,
  authState,
  createError,
  updateError,
  deleteError,
  isPlaying = false,
  onPlaybackControl,
  freezeTimeForNewNote = false
}): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(() => {
    const stored = localStorage.getItem('notes_sidebar_expanded');
    return stored ? JSON.parse(stored) : true;
  });

  // Bottom edit section state
  const [editContent, setEditContent] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [labelDropdown, setLabelDropdown] = useState<{
    noteId: string;
    position: { 
      x: number; 
      y: number; 
      buttonRect?: {
        top: number;
        bottom: number;
        left: number;
        right: number;
        height: number;
      };
    };
  } | null>(null);

  // New state for edit section labels dropdown
  const [editLabelsDropdown, setEditLabelsDropdown] = useState<{
    position: { 
      x: number; 
      y: number; 
      buttonRect?: {
        top: number;
        bottom: number;
        left: number;
        right: number;
        height: number;
      };
    };
  } | null>(null);

  // Temporary labels state for editing (not saved until note is saved)
  const [tempLabels, setTempLabels] = useState<Record<string, string>>({});

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    noteId: string;
    x: number;
    y: number;
  } | null>(null);

  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const [roundChangeFromNoteClick, setRoundChangeFromNoteClick] = useState(false);

  // Auto-pause/resume state
  const [wasPlayingBeforeEdit, setWasPlayingBeforeEdit] = useState(false);

  // Frozen timestamp state for new notes
  const [frozenTimestamp, setFrozenTimestamp] = useState<number | null>(null);
  const [isCreatingNewNote, setIsCreatingNewNote] = useState(false);


  // Labels state
  const [availableLabels, setAvailableLabels] = useState<Label[]>([]);
  // Filter state: record of labelName -> checked
  const [selectedLabelFilter, setSelectedLabelFilter] = useState<Record<string, boolean>>({});
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const notesBodyRef = useRef<HTMLDivElement>(null);
  const notesListRef = useRef<HTMLDivElement>(null);
  const noteRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [previousRound, setPreviousRound] = useState<number | null>(null);

  // Reset UI state when map changes
  useEffect(() => {
    const handleReset = () => {
      // Reset all UI state
      setEditContent('');
      setEditingNoteId(null);
      setSubmitError(null);
      setLabelDropdown(null);
      setEditLabelsDropdown(null);
      setTempLabels({});
      setContextMenu(null);
      setActiveNoteId(null);
      setRoundChangeFromNoteClick(false);
      setPreviousRound(null);
      setWasPlayingBeforeEdit(false);
      setFrozenTimestamp(null);
      setIsCreatingNewNote(false);
    };

    window.addEventListener('resetNotesUI', handleReset);
    return () => window.removeEventListener('resetNotesUI', handleReset);
  }, []);

  // Listen for focus notes event from hotkey
  useEffect(() => {
    const handleFocusNotes = () => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    };

    window.addEventListener('focusNotesTextarea', handleFocusNotes);
    return () => window.removeEventListener('focusNotesTextarea', handleFocusNotes);
  }, []);

  // Helper function to merge default labels with API labels
  const mergeLabelsWithDefaults = useCallback((apiLabels: Label[]): Label[] => {
    return apiLabels;
  }, []);

  useEffect(() => {
    localStorage.setItem('notes_sidebar_expanded', JSON.stringify(isExpanded));
  }, [isExpanded]);

  // Load available labels when user has PRO subscription
  useEffect(() => {
    loadLabels();
  }, [authState]);

  // Focus textarea when editing starts
  useEffect(() => {
    if (editingNoteId && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
    }
  }, [editingNoteId]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);

  // Close label dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setLabelDropdown(null);
    if (labelDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [labelDropdown]);

  // Close edit labels dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setEditLabelsDropdown(null);
    if (editLabelsDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [editLabelsDropdown]);

  // Update temporary labels when editing starts
  useEffect(() => {
    if (editingNoteId) {
      const note = sessionNotes.find(n => n.note_id === editingNoteId);
      setTempLabels(note?.labels || {});
    } else {
      setTempLabels({});
    }
  }, [editingNoteId, sessionNotes]);

  // Helper function to find which round a global time belongs to (closest round in the past)
  const getRoundForTime = useCallback((globalTime: number): number | undefined => {
    if (!roundInfo) return undefined;

    // Find the closest round in the past (round_start <= globalTime)
    // Sort by round_start descending to find the most recent one
    const pastRounds = roundInfo.rounds
      .filter(round => round.round_start <= globalTime)
      .sort((a, b) => b.round_start - a.round_start);

    return pastRounds.length > 0 ? pastRounds[0].round_number : undefined;
  }, [roundInfo]);

  // Auto-scroll to first note of current round when round changes
  useEffect(() => {
    if (!roundInfo || !isExpanded || loading) return;

    const currentRound = roundInfo.currentRound;
    
    // Only scroll if round actually changed and we have notes
    if (previousRound !== null && previousRound !== currentRound && sessionNotes.length > 0) {
      // Find the first note for the current round
      const firstNoteForRound = sessionNotes.find(note => {
        const noteRound = getRoundForTime(note.global_offset);
        return noteRound === currentRound;
      });

      if (firstNoteForRound) {
        // If round change was NOT from a note click, make the first note active
        if (!roundChangeFromNoteClick) {
          setActiveNoteId(firstNoteForRound.note_id);
        }
        
        // Scroll to the note with a small delay to ensure DOM is updated
        setTimeout(() => {
          const noteElement = noteRefs.current.get(firstNoteForRound.note_id);
          
          if (noteElement && notesListRef.current) {
            const containerTop = notesListRef.current.scrollTop;
            const containerHeight = notesListRef.current.clientHeight;
            const noteTop = noteElement.offsetTop;
            const noteHeight = noteElement.offsetHeight;

            // Calculate if note is already visible
            const noteBottom = noteTop + noteHeight;
            const containerBottom = containerTop + containerHeight;

            // Scroll if note is not fully visible
            if (noteTop < containerTop || noteBottom > containerBottom) {
              // Calculate header height to avoid scrolling behind it
              const headerHeight = 80; // Approximate header height including padding
              const scrollPosition = Math.max(0, noteTop - headerHeight);
              
              // Try multiple scroll methods
              try {
                // Method 1: scrollTo with options (smooth)
                notesListRef.current.scrollTo({
                  top: scrollPosition,
                  behavior: 'smooth'
                });
              } catch (error) {
                // Method 2: Direct scrollTop assignment
                notesListRef.current.scrollTop = scrollPosition;
              }
            }
          }
        }, 200); // Increased delay to 200ms
      }
      
      // Reset the flag after processing the round change
      setRoundChangeFromNoteClick(false);
    }

    setPreviousRound(currentRound);
  }, [roundInfo?.currentRound, sessionNotes, isExpanded, loading, roundChangeFromNoteClick, getRoundForTime]);

  // Set ref for note elements
  const setNoteRef = (noteId: string, element: HTMLDivElement | null) => {
    if (element) {
      noteRefs.current.set(noteId, element);
    } else {
      noteRefs.current.delete(noteId);
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatRoundTime = (seconds: number): string => {
    if (seconds >= 100) return '0:00';
    const roundTime = 100 - Math.floor(seconds);
    const minutes = Math.floor(roundTime / 60);
    const remainingSeconds = roundTime % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Convert global time to round + local time display
  const getTimeDisplay = (globalTime: number): { roundNumber?: number; localTime: string; isCurrentRound: boolean } => {
    if (!roundInfo) {
      return { localTime: formatTime(globalTime), isCurrentRound: false };
    }

    const roundNumber = getRoundForTime(globalTime);
    if (roundNumber !== undefined) {
      // Find the round data to calculate local time
      const matchedRound = roundInfo.rounds.find(r => r.round_number === roundNumber);
      if (matchedRound) {
        // Check if time is within round bounds
        if (globalTime >= matchedRound.round_start && globalTime <= matchedRound.round_end) {
          const localTime = globalTime - matchedRound.round_start;
          return { 
            roundNumber, 
            localTime: formatRoundTime(localTime),
            isCurrentRound: roundNumber === roundInfo.currentRound
          };
        } else {
          // Time is not within round bounds, show 0:00
          return { 
            roundNumber, 
            localTime: '0:00',
            isCurrentRound: roundNumber === roundInfo.currentRound
          };
        }
      }
    }

    // If no round in the past, fall back to global time
    return { localTime: formatTime(globalTime), isCurrentRound: false };
  };

  const renderTimeElements = (note: SessionNote) => {
    const timeInfo = getTimeDisplay(note.global_offset);

    if (timeInfo.roundNumber) {
      const isDoubleDigit = timeInfo.roundNumber >= 10;
      return (
        <div className={styles.timeElements}>
          <span 
            className={`${styles.roundNumber} ${
              timeInfo.isCurrentRound ? styles.currentRound : styles.notCurrentRound
            } ${isDoubleDigit ? styles.doubleDigit : ''}`}
            data-number={isDoubleDigit ? timeInfo.roundNumber : undefined}
          >
            {isDoubleDigit ? '' : timeInfo.roundNumber}
          </span>
          <span className={`${styles.roundTime} ${
            timeInfo.isCurrentRound ? styles.currentRoundTime : styles.notCurrentRoundTime
          }`}>
            {timeInfo.localTime}
          </span>
        </div>
      );
    } else {
      return (
        <span className={`${styles.globalTime} ${styles.notCurrentRoundTime}`}>
          {timeInfo.localTime}
        </span>
      );
    }
  };

  const handleContextMenu = (event: React.MouseEvent, noteId: string) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Set this note as active when opening context menu
    setActiveNoteId(noteId);
    
    // If clicking the same button that already has the menu open, close it
    if (contextMenu && contextMenu.noteId === noteId) {
      setContextMenu(null);
      return;
    }
    
    const button = event.currentTarget as HTMLElement;
    const buttonRect = button.getBoundingClientRect();
    
    setContextMenu({
      noteId,
      x: buttonRect.right - 120, // Align right edges (menu width is 120px)
      y: buttonRect.bottom + 4 // Position just below the button
    });
  };

  const startEditing = (note: SessionNote) => {
    setEditingNoteId(note.note_id);
    setEditContent(note.content);
    setContextMenu(null);
    setSubmitError(null);
  };

  const cancelEditing = () => {
    // Resume playback if we paused it for editing
    if (wasPlayingBeforeEdit && onPlaybackControl) {
      onPlaybackControl(true);
      setWasPlayingBeforeEdit(false);
    }

    setEditingNoteId(null);
    setEditContent('');
    setSubmitError(null);
    setTempLabels({});
    setEditLabelsDropdown(null);
    setFrozenTimestamp(null);
    setIsCreatingNewNote(false);
  };

  const handleTextareaFocus = () => {
    // Auto-pause playback when user starts editing (only if not already editing)
    // But don't pause if we're in freeze mode
    if (!editingNoteId && isPlaying && onPlaybackControl && !freezeTimeForNewNote) {
      setWasPlayingBeforeEdit(true);
      onPlaybackControl(false);
    }
    
    // Freeze timestamp when user starts creating a new note (not editing existing)
    if (!editingNoteId && freezeTimeForNewNote && frozenTimestamp === null) {
      setFrozenTimestamp(currentTime);
      setIsCreatingNewNote(true);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditContent(e.target.value);
    
    // Auto-pause on first keystroke if not already paused
    // But don't pause if we're in freeze mode
    if (!editingNoteId && e.target.value.length === 1 && isPlaying && onPlaybackControl && !freezeTimeForNewNote) {
      setWasPlayingBeforeEdit(true);
      onPlaybackControl(false);
    }
    
    // Freeze timestamp on first keystroke if not already frozen
    if (!editingNoteId && freezeTimeForNewNote && e.target.value.length === 1 && frozenTimestamp === null) {
      setFrozenTimestamp(currentTime);
      setIsCreatingNewNote(true);
    }
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Save note on Ctrl+Enter
    if (e.ctrlKey && e.key === 'Enter') {
      e.preventDefault();
      if (editContent.trim()) {
        handleSubmit();
        // Unfocus the textarea after saving
        if (textareaRef.current) {
          textareaRef.current.blur();
        }
      }
    }
  };

  const handleSubmit = async () => {
    if (!editContent.trim()) return;

    setSubmitError(null);

    // Store values we need before clearing the form
    const contentToSubmit = editContent;
    const labelsToSubmit = { ...tempLabels };
    const editingNoteIdToSubmit = editingNoteId;

    // Resume playback if we paused it for editing
    if (wasPlayingBeforeEdit && onPlaybackControl) {
      onPlaybackControl(true);
      setWasPlayingBeforeEdit(false);
    }

    // Clear form immediately for better UX with optimistic updates
    setEditingNoteId(null);
    setEditContent('');
    setTempLabels({});
    setEditLabelsDropdown(null);
    setFrozenTimestamp(null);
    setIsCreatingNewNote(false);

    try {
      if (editingNoteIdToSubmit) {
        // Editing existing note - preserve original timing but use temporary labels
        const originalNote = sessionNotes.find(note => note.note_id === editingNoteIdToSubmit);
        if (!originalNote) return;
        
        const originalTime = originalNote.global_offset;
        // Don't await - let optimistic update handle the UI
        onUpdateNote(editingNoteIdToSubmit, contentToSubmit, originalTime, labelsToSubmit).catch(err => {
          console.error('Error updating note:', err);
          setSubmitError('Failed to update note');
        });
      } else {
        // Creating new note - generate ID immediately and set as active
        const newNoteId = generateNoteId();
        
        // Set the new note as active immediately
        setActiveNoteId(newNoteId);
        
        // Use frozen timestamp if available, otherwise use current time
        const noteTimestamp = frozenTimestamp !== null ? frozenTimestamp : currentTime;
        
        // Don't await - let optimistic update handle the UI
        onCreateNote(contentToSubmit, noteTimestamp, labelsToSubmit, newNoteId).then(() => {
          // Scroll to the new note after a short delay
          setTimeout(() => {
            const noteElement = noteRefs.current.get(newNoteId);
            if (noteElement && notesListRef.current) {
              const containerTop = notesListRef.current.scrollTop;
              const containerHeight = notesListRef.current.clientHeight;
              const noteTop = noteElement.offsetTop;
              const noteHeight = noteElement.offsetHeight;
              
              // Calculate if note is already visible
              const noteBottom = noteTop + noteHeight;
              const containerBottom = containerTop + containerHeight;
              
              // Scroll if note is not fully visible
              if (noteTop < containerTop || noteBottom > containerBottom) {
                const headerHeight = 80; // Approximate header height including padding
                const scrollPosition = Math.max(0, noteTop - headerHeight);
                
                try {
                  notesListRef.current.scrollTo({
                    top: scrollPosition,
                    behavior: 'smooth'
                  });
                } catch (error) {
                  notesListRef.current.scrollTop = scrollPosition;
                }
              }
            }
          }, 100); // Small delay to ensure DOM is updated
        }).catch(err => {
          console.error('Error creating note:', err);
          setSubmitError('Failed to create note');
        });
      }
    } catch (err) {
      console.error('Error submitting note:', err);
      setSubmitError(editingNoteIdToSubmit ? 'Failed to update note' : 'Failed to create note');
    }
  };

  const handleDelete = async (noteId: string) => {
    setContextMenu(null);
    setSubmitError(null);

    try {
      await onDeleteNote(noteId);
    } catch (err) {
      console.error('Error deleting note:', err);
      setSubmitError('Failed to delete note');
    }
  };

  const addLabelToPalette = async (labelName: string, color: string) => {
    try {
      await fetchAPI('/labels', {
        method: 'POST',
        body: JSON.stringify({
          label: labelName,
          color: color
        })
      });
      
      // Reload labels after successful addition
      await loadLabels();
    } catch (error) {
      console.error('Error adding label to palette:', error);
      throw error;
    }
  };

  const removeLabelFromPalette = async (labelName: string) => {
    try {
      await fetchAPI(`/labels/${labelName.toLowerCase()}`, {
        method: 'DELETE'
      });
      
      // Reload labels after successful removal
      await loadLabels();
    } catch (error) {
      console.error('Error removing label from palette:', error);
      throw error;
    }
  };

  const loadLabels = async () => {
    if (authState.progress === 'DONE' && authState.userStatus === 'PRO') {
      try {
        const response = await fetchAPI('/labels');
        const apiLabels = response.labels || [];
        setAvailableLabels(mergeLabelsWithDefaults(apiLabels));
      } catch (error) {
        console.error('Error loading labels:', error);
        // If API fails, at least show the default labels
        setAvailableLabels(mergeLabelsWithDefaults([]));
      }
    }
  };

  const handleAddLabelToNote = async (noteId: string, labelName: string, labelColor: string) => {
    // Close dropdown
    setLabelDropdown(null);

    try {
      await onAddLabelToNote(noteId, labelName, labelColor);
    } catch (error) {
      console.error('Error adding label to note:', error);
    }
  };

  const handleRemoveLabelFromNote = async (noteId: string, labelName: string) => {
    try {
      await onRemoveLabelFromNote(noteId, labelName);
    } catch (error) {
      console.error('Error removing label from note:', error);
    }
  };

  const handleRemoveLabelClick = (noteId: string, labelName: string) => {
    handleRemoveLabelFromNote(noteId, labelName);
  };

  // Handlers for temporary labels during editing
  const handleTempAddLabel = (labelName: string, labelColor: string) => {
    setTempLabels(prev => ({ ...prev, [labelName]: labelColor }));
  };

  const handleTempRemoveLabel = (labelName: string) => {
    setTempLabels(prev => {
      const updated = { ...prev };
      delete updated[labelName];
      return updated;
    });
  };

  const handleEditLabelsToggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    if(e.type !== 'click') {
      return;
    }
    if(e.type === 'click' && editLabelsDropdown) {
      setEditLabelsDropdown(null);
      return;
    }
    
    const button = e.currentTarget as HTMLElement;
    const buttonRect = button.getBoundingClientRect();
    
    setEditLabelsDropdown({
      position: {
        x: buttonRect.left,
        y: buttonRect.bottom + 4,
        buttonRect: {
          top: buttonRect.top,
          bottom: buttonRect.bottom,
          left: buttonRect.left,
          right: buttonRect.right,
          height: buttonRect.height
        }
      }
    });
  };

  const handleAddLabelClick = (e: React.MouseEvent, noteId: string) => {
    e.stopPropagation();
    
    if(e.type !== 'click') {
      return;
    }
    if(e.type === 'click' && labelDropdown) {
      setLabelDropdown(null);
      return;
    }

    const button = e.currentTarget as HTMLElement;
    const buttonRect = button.getBoundingClientRect();
    
    setLabelDropdown({
      noteId,
      position: {
        x: buttonRect.left,
        y: buttonRect.bottom + 4,
        buttonRect: {
          top: buttonRect.top,
          bottom: buttonRect.bottom,
          left: buttonRect.left,
          right: buttonRect.right,
          height: buttonRect.height
        }
      }
    });
  };

  const renderNote = (note: SessionNote) => {
    const timeInfo = getTimeDisplay(note.global_offset);
    const isCurrentRound = timeInfo.isCurrentRound;
    const isBeingEdited = editingNoteId === note.note_id;
    const isActive = activeNoteId === note.note_id;
    
    const handleNoteClick = (e: React.MouseEvent) => {
      // Don't trigger click if clicking on menu button or during context menu
      if (e.target && (e.target as HTMLElement).closest(`.${styles.menuButton}`)) {
        return;
      }
      
      // Note clicks are always allowed in optimistic mode
      
      // Set this note as active
      setActiveNoteId(note.note_id);
      
      // Check if this note click will trigger a round change
      if (timeInfo.roundNumber && roundInfo && timeInfo.roundNumber !== roundInfo.currentRound) {
        // This note click will trigger a round change, so set the flag
        setRoundChangeFromNoteClick(true);
      }
      
      if (onNoteClick) {
        onNoteClick(note.global_offset, timeInfo.roundNumber, note.youtube_id);
      }
    };

    const handleNoteDoubleClick = (e: React.MouseEvent) => {
      // Don't trigger double-click if clicking on menu button or during context menu
      if (e.target && (e.target as HTMLElement).closest(`.${styles.menuButton}`)) {
        return;
      }
      
      // Enter edit mode for this note
      startEditing(note);
    };
    
    return (
      <div 
        className={`${styles.noteItem} ${isBeingEdited ? styles.editing : ''} ${isActive && !isBeingEdited ? styles.active : ''}`}
        ref={(element) => setNoteRef(note.note_id, element)}
        onClick={handleNoteClick}
        onDoubleClick={handleNoteDoubleClick}
      >
        <div className={styles.noteHeader}>
          <div className={styles.noteHeaderLeft}>
            {renderTimeElements(note)}
            <Labels 
              labels={note.labels || {}}
              showAddButton={authState.userStatus === 'PRO'}
              removable={authState.userStatus === 'PRO'}
              onRemoveLabel={(labelName) => handleRemoveLabelClick(note.note_id, labelName)}
              onAddLabel={(e) => handleAddLabelClick(e, note.note_id)}
              isCurrentRound={isCurrentRound}
            />
          </div>
          <button
            className={styles.menuButton}
            onClick={(e) => handleContextMenu(e, note.note_id)}
            title="Options"
          >
            <i className="bi bi-three-dots"></i>
          </button>
        </div>
        <div className={`${styles.noteContent} ${isCurrentRound ? styles.currentRound : styles.notCurrentRound}`}>
          {note.content}
        </div>
      </div>
    );
  };

  const renderContextMenu = () => {
    if (!contextMenu) return null;

    return (
      <div
        className={styles.contextMenu}
        style={{
          position: 'fixed',
          left: contextMenu.x,
          top: contextMenu.y,
          zIndex: 1000
        }}
      >
        <button
          className={styles.contextMenuItem}
          onClick={() => {
            const note = sessionNotes.find(n => n.note_id === contextMenu.noteId);
            if (note) startEditing(note);
          }}
        >
          <i className="bi bi-pencil"></i>
          Edit
        </button>
        <button
          className={styles.contextMenuItem}
          onClick={() => handleDelete(contextMenu.noteId)}
        >
          <i className="bi bi-trash"></i>
          Remove
        </button>
      </div>
    );
  };

  // Compute filtered notes based on selected labels (OR logic)
  const filteredNotes = (() => {
    const anyChecked = Object.values(selectedLabelFilter).some(Boolean);
    if (!anyChecked) return sessionNotes;
    return sessionNotes.filter(note => {
      const noteLabels = note.labels || {};
      for (const name of Object.keys(noteLabels)) {
        if (selectedLabelFilter[name]) return true;
      }
      return false;
    });
  })();

  return (
    <div className={`${styles.notesSidebar} ${isExpanded ? styles.expanded : styles.collapsed}`}>
      <button 
        className={styles.toggleButton} 
        onClick={toggleExpanded}
        title={isExpanded ? undefined : "Notes"}
      >
        <i className={`bi bi-${isExpanded ? 'chevron-right' : 'journal-text'}`} />
      </button>
      
      <div className={styles.notesContent}>
        <div className={styles.notesHeader}>
          <h3>
            <i className="bi bi-journal-text"></i>
            Notes
          </h3>
        </div>
        {/* Label filter dropdown between header and notes */}
        {authState.userStatus === 'PRO' && (
          <LabelFilterDropdown
            availableLabels={availableLabels}
            selected={selectedLabelFilter}
            onChange={setSelectedLabelFilter}
          />
        )}
        
        <div className={styles.notesBody} ref={notesBodyRef}>
          {loading && (
            <div className={styles.loading}>
              <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
              Loading notes...
            </div>
          )}
          
          {!loading && authState.progress === 'DONE' && authState.userStatus !== 'PRO' && (
            <div className={styles.emptyState}>
              <i className="bi bi-journal-text" style={{ fontSize: '2rem', color: 'var(--textTertiary)', marginBottom: '1rem' }}></i>
              <p>Notes are currently unavailable for this account.</p>
              <p className={styles.emptyStateHint}>
                Take notes at specific moments in matches
              </p>
            </div>
          )}
          
          {/* Error alerts */}
          {error && (
            <Alert variant="danger" className="mx-2 mt-2">
              <Alert.Heading className="h6">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                Error Loading Notes
              </Alert.Heading>
              {error}
            </Alert>
          )}
          
          {createError && (
            <Alert variant="danger" className="mx-2 mt-2">
              <Alert.Heading className="h6">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                Error Creating Note
              </Alert.Heading>
              {createError}
            </Alert>
          )}
          
          {updateError && (
            <Alert variant="warning" className="mx-2 mt-2">
              <Alert.Heading className="h6">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                Error Updating Note
              </Alert.Heading>
              {updateError}
            </Alert>
          )}
          
          {deleteError && (
            <Alert variant="danger" className="mx-2 mt-2">
              <Alert.Heading className="h6">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                Error Deleting Note
              </Alert.Heading>
              {deleteError}
            </Alert>
          )}
          
          {!loading && !error && authState.userStatus === 'PRO' && sessionNotes.length === 0 && (
            <div className={styles.emptyState}>
              <p>No notes yet</p>
              <p className={styles.emptyStateHint}>Add your first note using the form below</p>
            </div>
          )}
          
          {!loading && !error && authState.userStatus === 'PRO' && filteredNotes.length > 0 && (
            <div className={styles.notesList} ref={notesListRef}>
              {filteredNotes.map(renderNote)}
            </div>
          )}
          {!loading && !error && authState.userStatus === 'PRO' && sessionNotes.length > 0 && filteredNotes.length === 0 && (
            <div className={styles.emptyState}>
              <p>No notes match current filters</p>
              <p className={styles.emptyStateHint}>Clear filters to see all notes</p>
            </div>
          )}
        </div>

        {/* Bottom Edit Section - Only show for Pro users */}
        {authState.userStatus === 'PRO' && (
          <div className={styles.editSection}>
            <textarea
              ref={textareaRef}
              className={`${styles.editTextarea} editTextarea`}
              value={editContent}
              onChange={handleTextareaChange}
              onFocus={handleTextareaFocus}
              onKeyDown={handleTextareaKeyDown}
              placeholder={editingNoteId ? "Edit your note..." : "Add a note at current time..."}
              rows={6}
            />
            
            <div className={styles.editActions}>
              {submitError && (
                <div className={styles.errorMessage}>
                  <i className="bi bi-exclamation-triangle" />
                  <span>{submitError}</span>
                </div>
              )}
              
              <div className={styles.actionButtons}>
                <div className={styles.labelsButtonContainer}>
                  <button
                    className={styles.labelsButton}
                    onMouseDown={handleEditLabelsToggle}
                    onClick={handleEditLabelsToggle}
                  >
                    <i className="bi bi-tags"></i>
                    <span>Labels</span>
                    <span className={styles.labelCount}>({Object.keys(tempLabels).length})</span>
                  </button>
                </div>

                <div className={styles.mainActionButtons}>
                    {/* New cancel button for freezing mode - only show when creating new note with frozen timestamp */}
                  {!editingNoteId && isCreatingNewNote && frozenTimestamp !== null && (
                    <button
                      className={styles.cancelButton}
                      onClick={cancelEditing}
                      title="Cancel creating new note"
                    >
                      Cancel
                    </button>
                  )}

                  <button
                    className={`${styles.submitButton} ${editingNoteId || editContent.trim() ? styles.saveButton : styles.addButton}`}
                    onClick={handleSubmit}
                    disabled={!editContent.trim()}
                    title={editingNoteId ? "Ctrl+Enter to save" : undefined}
                  >
                    <i className={`bi bi-${editingNoteId ? 'check-lg' : 'pencil'}`}></i>
                    {editingNoteId ? 'Save' : (
                      <>
                        <span>Add Note</span>
                        <span className={styles.buttonTiming}>({getTimeDisplay(frozenTimestamp !== null ? frozenTimestamp : currentTime).localTime})</span>
                      </>
                    )}
                  </button>
                  
                  {/* Existing cancel button for editing mode */}
                  {editingNoteId && (
                    <button
                      className={styles.cancelButton}
                      onClick={cancelEditing}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {renderContextMenu()}
      
      {/* Label dropdown */}
      {labelDropdown && (() => {
        const note = sessionNotes.find(n => n.note_id === labelDropdown.noteId);
        if (!note) return null;
        
        return (
          <AddLabelDropdown
            availableLabels={availableLabels}
            existingLabels={note.labels || {}}
            position={labelDropdown.position}
            onAddLabel={(labelName, labelColor) => handleAddLabelToNote(note.note_id, labelName, labelColor)}
            onClose={() => setLabelDropdown(null)}
            onAddLabelToPalette={addLabelToPalette}
            onRemoveLabelFromPalette={removeLabelFromPalette}
          />
        );
      })()}

      {/* Edit Labels dropdown for edit section */}
      {editLabelsDropdown && (
        <AddLabelDropdown
          availableLabels={availableLabels}
          existingLabels={tempLabels}
          position={editLabelsDropdown.position}
          mode="checklist"
          onAddLabel={handleTempAddLabel}
          onRemoveLabel={handleTempRemoveLabel}
          onClose={() => setEditLabelsDropdown(null)}
          onAddLabelToPalette={addLabelToPalette}
          onRemoveLabelFromPalette={removeLabelFromPalette}
        />
      )}
    </div>
  );
};

export default NotesUI; 